import { normalizeToIsoZMidnight } from './dateNormalization'

export type MortalityPerformanceSnapshotInput = {
  lotToken: string
  stage: 'Active' | 'Inactive'
  date: string
  observedMortalityRate: number | null
  expectedMortalityRate: number | null
  observedMortalitySource?: 'mortalityLog' | 'productionSummary' | null
}

export type MortalityPerformanceSnapshot = {
  asOfDate: string
  activeLotCount: number
  currentObservedMortalityRate: number | null
  previousObservedMortalityRate: number | null
  currentExpectedMortalityRate: number | null
  trendDeltaPts: number | null
  mortalityGapPts: number | null
  trend: 'improving' | 'worsening' | 'stable' | 'insufficient-history'
  currentSampleCount: number
  previousSampleCount: number
  currentMortalityLogSampleCount: number
  previousMortalityLogSampleCount: number
  currentMortalityLogLatestDate: string | null
  previousMortalityLogLatestDate: string | null
  currentObservedSourceMode: 'mortality-log-only' | 'mixed' | 'summary-fallback' | 'no-observed'
  previousObservedSourceMode: 'mortality-log-only' | 'mixed' | 'summary-fallback' | 'no-observed'
  currentMortalityLogRecencyRisk: 'fresh' | 'aging' | 'stale' | 'unknown'
  previousMortalityLogRecencyRisk: 'fresh' | 'aging' | 'stale' | 'unknown'
  trustSummary: 'strong' | 'caution' | 'weak'
  trustReason:
    | 'current-summary-fallback'
    | 'previous-no-observed'
    | 'previous-stale'
    | 'current-no-observed'
    | 'both-log-backed-recent'
    | 'mixed-evidence'
}

const DAY_MS = 86_400_000

const FRESH_DAYS = 2
const AGING_DAYS = 7

type WindowRates = {
  observedAverage: number | null
  observedCount: number
  expectedAverage: number | null
  mortalityLogObservedCount: number
  mortalityLogLatestDate: string | null
  observedSourceMode: 'mortality-log-only' | 'mixed' | 'summary-fallback' | 'no-observed'
}

const deriveObservedSourceMode = (
  observedCount: number,
  mortalityLogObservedCount: number
): WindowRates['observedSourceMode'] => {
  if (observedCount <= 0) return 'no-observed'
  if (mortalityLogObservedCount <= 0) return 'summary-fallback'
  if (mortalityLogObservedCount >= observedCount) return 'mortality-log-only'
  return 'mixed'
}

const toUtcDateTime = (value: string): number | null => {
  const normalized = normalizeToIsoZMidnight(value)
  if (!normalized.ok) return null

  const [yearStr, monthStr, dayStr] = normalized.isoDate.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null
  }

  return Date.UTC(year, month - 1, day)
}

const deriveMortalityLogRecencyRisk = (
  latestDate: string | null,
  asOfUtcDate: number
): MortalityPerformanceSnapshot['currentMortalityLogRecencyRisk'] => {
  if (!latestDate) return 'unknown'

  const latestUtcDate = toUtcDateTime(latestDate)
  if (latestUtcDate === null) return 'unknown'

  const dayGap = Math.floor((asOfUtcDate - latestUtcDate) / DAY_MS)
  if (!Number.isFinite(dayGap) || dayGap < 0) return 'unknown'
  if (dayGap <= FRESH_DAYS) return 'fresh'
  if (dayGap <= AGING_DAYS) return 'aging'
  return 'stale'
}

const deriveTrustSummary = (args: {
  currentObservedSourceMode: MortalityPerformanceSnapshot['currentObservedSourceMode']
  previousObservedSourceMode: MortalityPerformanceSnapshot['previousObservedSourceMode']
  currentMortalityLogRecencyRisk: MortalityPerformanceSnapshot['currentMortalityLogRecencyRisk']
  previousMortalityLogRecencyRisk: MortalityPerformanceSnapshot['previousMortalityLogRecencyRisk']
}): MortalityPerformanceSnapshot['trustSummary'] => {
  if (args.currentObservedSourceMode === 'no-observed') return 'weak'
  if (args.currentMortalityLogRecencyRisk === 'stale') return 'weak'

  if (args.currentObservedSourceMode === 'summary-fallback') return 'caution'
  if (args.currentMortalityLogRecencyRisk === 'unknown') return 'caution'
  if (args.previousObservedSourceMode === 'no-observed') return 'caution'
  if (args.previousMortalityLogRecencyRisk === 'unknown') return 'caution'
  if (args.previousMortalityLogRecencyRisk === 'stale') return 'caution'
  if (args.currentMortalityLogRecencyRisk === 'aging') return 'caution'
  if (args.previousMortalityLogRecencyRisk === 'aging') return 'caution'

  return 'strong'
}

const deriveTrustReason = (args: {
  currentObservedSourceMode: MortalityPerformanceSnapshot['currentObservedSourceMode']
  previousObservedSourceMode: MortalityPerformanceSnapshot['previousObservedSourceMode']
  currentMortalityLogRecencyRisk: MortalityPerformanceSnapshot['currentMortalityLogRecencyRisk']
  previousMortalityLogRecencyRisk: MortalityPerformanceSnapshot['previousMortalityLogRecencyRisk']
  trustSummary: MortalityPerformanceSnapshot['trustSummary']
}): MortalityPerformanceSnapshot['trustReason'] => {
  if (args.currentObservedSourceMode === 'no-observed') return 'current-no-observed'
  if (args.currentObservedSourceMode === 'summary-fallback') return 'current-summary-fallback'
  if (args.previousObservedSourceMode === 'no-observed') return 'previous-no-observed'
  if (args.previousMortalityLogRecencyRisk === 'stale') return 'previous-stale'
  if (args.trustSummary === 'strong') return 'both-log-backed-recent'
  return 'mixed-evidence'
}

const computeWindowRates = (
  rows: Array<MortalityPerformanceSnapshotInput & { utcDate: number }>,
  fromUtcInclusive: number,
  toUtcInclusive: number
): WindowRates => {
  let observedSum = 0
  let observedCount = 0
  let expectedSum = 0
  let expectedCount = 0
  let mortalityLogObservedCount = 0
  let mortalityLogLatestUtc = Number.NEGATIVE_INFINITY
  let mortalityLogLatestDate: string | null = null

  for (const row of rows) {
    if (row.utcDate < fromUtcInclusive || row.utcDate > toUtcInclusive) continue

    if (row.observedMortalityRate !== null) {
      observedSum += row.observedMortalityRate
      observedCount += 1
      if (row.observedMortalitySource === 'mortalityLog') {
        mortalityLogObservedCount += 1
        if (row.utcDate >= mortalityLogLatestUtc) {
          mortalityLogLatestUtc = row.utcDate
          const normalized = normalizeToIsoZMidnight(row.date)
          mortalityLogLatestDate = normalized.ok ? normalized.isoDate : row.date
        }
      }
    }

    if (row.expectedMortalityRate !== null) {
      expectedSum += row.expectedMortalityRate
      expectedCount += 1
    }
  }

  return {
    observedAverage: observedCount > 0 ? observedSum / observedCount : null,
    observedCount,
    expectedAverage: expectedCount > 0 ? expectedSum / expectedCount : null,
    mortalityLogObservedCount,
    mortalityLogLatestDate,
    observedSourceMode: deriveObservedSourceMode(observedCount, mortalityLogObservedCount)
  }
}

export const deriveMortalityPerformanceSnapshot = (
  kpis: MortalityPerformanceSnapshotInput[],
  windowDays = 7
): MortalityPerformanceSnapshot | null => {
  const effectiveWindowDays = Number.isFinite(windowDays) && windowDays > 0
    ? Math.floor(windowDays)
    : 7

  const activeRows = kpis
    .filter((kpi) => kpi.stage === 'Active')
    .map((kpi) => {
      const utcDate = toUtcDateTime(kpi.date)
      return utcDate === null ? null : { ...kpi, utcDate }
    })
    .filter((kpi): kpi is MortalityPerformanceSnapshotInput & { utcDate: number } => Boolean(kpi))

  if (activeRows.length === 0) return null

  let latestUtcDate = Number.NEGATIVE_INFINITY
  let latestIsoDate = ''

  for (const row of activeRows) {
    if (row.utcDate > latestUtcDate) {
      latestUtcDate = row.utcDate
      const normalized = normalizeToIsoZMidnight(row.date)
      latestIsoDate = normalized.ok ? normalized.isoDate : row.date
    }
  }

  if (!Number.isFinite(latestUtcDate)) return null

  const currentStart = latestUtcDate - (effectiveWindowDays - 1) * DAY_MS
  const previousEnd = currentStart - DAY_MS
  const previousStart = previousEnd - (effectiveWindowDays - 1) * DAY_MS

  const current = computeWindowRates(activeRows, currentStart, latestUtcDate)
  const previous = computeWindowRates(activeRows, previousStart, previousEnd)

  const trendDeltaPts = current.observedAverage !== null && previous.observedAverage !== null
    ? current.observedAverage - previous.observedAverage
    : null

  const mortalityGapPts = current.observedAverage !== null && current.expectedAverage !== null
    ? current.observedAverage - current.expectedAverage
    : null

  let trend: MortalityPerformanceSnapshot['trend'] = 'insufficient-history'
  if (trendDeltaPts !== null) {
    if (trendDeltaPts < -0.01) trend = 'improving'
    else if (trendDeltaPts > 0.01) trend = 'worsening'
    else trend = 'stable'
  }

  const activeLotCount = new Set(activeRows.map((row) => row.lotToken)).size
  const currentMortalityLogRecencyRisk = deriveMortalityLogRecencyRisk(current.mortalityLogLatestDate, latestUtcDate)
  const previousMortalityLogRecencyRisk = deriveMortalityLogRecencyRisk(previous.mortalityLogLatestDate, latestUtcDate)
  const trustSummary = deriveTrustSummary({
    currentObservedSourceMode: current.observedSourceMode,
    previousObservedSourceMode: previous.observedSourceMode,
    currentMortalityLogRecencyRisk,
    previousMortalityLogRecencyRisk
  })
  const trustReason = deriveTrustReason({
    currentObservedSourceMode: current.observedSourceMode,
    previousObservedSourceMode: previous.observedSourceMode,
    currentMortalityLogRecencyRisk,
    previousMortalityLogRecencyRisk,
    trustSummary
  })

  return {
    asOfDate: latestIsoDate,
    activeLotCount,
    currentObservedMortalityRate: current.observedAverage,
    previousObservedMortalityRate: previous.observedAverage,
    currentExpectedMortalityRate: current.expectedAverage,
    trendDeltaPts,
    mortalityGapPts,
    trend,
    currentSampleCount: current.observedCount,
    previousSampleCount: previous.observedCount,
    currentMortalityLogSampleCount: current.mortalityLogObservedCount,
    previousMortalityLogSampleCount: previous.mortalityLogObservedCount,
    currentMortalityLogLatestDate: current.mortalityLogLatestDate,
    previousMortalityLogLatestDate: previous.mortalityLogLatestDate,
    currentObservedSourceMode: current.observedSourceMode,
    previousObservedSourceMode: previous.observedSourceMode,
    currentMortalityLogRecencyRisk,
    previousMortalityLogRecencyRisk,
    trustSummary,
    trustReason
  }
}
